<template>
  <section class="space-y-6">
    <!-- Ürün listesi yüklenirken iskelet gösterimi -->
    <FrontendSkeletonProductCarousel v-if="loading" />

    <div v-else >
      <!-- Bannerlar üstte ayrı bir blok olarak render ediliyor -->
      <div v-if="bannerCount" class="ml-4 mb-6 md:ml-0" style="contain: layout style">
        <!-- Tek banner varsa sadece mobilde tam genişlik göster -->
        <div v-if="bannerCount === 1 && isMobile" class=" mr-4">
          <NuxtLink
            v-if="resolveBannerLink(displayBanners[0]) && !isBannerExternal(displayBanners[0])"
            :to="resolveBannerLink(displayBanners[0])"
            :class="singleMobileWrapperClass"
          >
            <NuxtImg format="webp" :src="displayBanners[0].src" :alt="displayBanners[0].alt" loading="lazy"
              :class="mobileSingleImageClass" />
          </NuxtLink>
          <a
            v-else-if="resolveBannerLink(displayBanners[0])"
            :href="resolveBannerLink(displayBanners[0])"
            target="_blank"
            rel="noopener noreferrer"
            :class="singleMobileWrapperClass"
          >
            <NuxtImg format="webp" :src="displayBanners[0].src" :alt="displayBanners[0].alt" loading="lazy"
              :class="mobileSingleImageClass" />
          </a>
          <div v-else :class="singleMobileWrapperClass">
            <NuxtImg format="webp" :src="displayBanners[0].src" :alt="displayBanners[0].alt" loading="lazy"
              :class="mobileSingleImageClass" />
          </div>
        </div>

        <!-- Birden fazla banner varsa mobilde swiper kullan -->
        <Swiper
          v-else-if="isMobile"
          :modules="bannerSwiperModules"
          :slides-per-view="'auto'"
          :space-between="16"
          :free-mode="bannerFreeModeOptions"
          :touch-ratio="1"
          :resistance="false"
          :watch-overflow="true"
          class="banner-swiper"
        >
          <SwiperSlide
            v-for="(banner, index) in displayBanners"
            :key="banner.src + index"
            class="!w-[calc(100vw-3rem)] sm:!w-[360px]"
          >
            <NuxtLink
              v-if="resolveBannerLink(banner) && !isBannerExternal(banner)"
              :to="resolveBannerLink(banner)"
              :class="sliderWrapperClass"
            >
              <NuxtImg format="webp" :src="banner.src" :alt="banner.alt" loading="lazy"
                :class="mobileSliderImageClass" />
            </NuxtLink>
            <a
              v-else-if="resolveBannerLink(banner)"
              :href="resolveBannerLink(banner)"
              target="_blank"
              rel="noopener noreferrer"
              :class="sliderWrapperClass"
            >
              <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy" format="webp"
                :class="mobileSliderImageClass" />
            </a>
            <div v-else :class="sliderWrapperClass">
              <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy" format="webp" 
                :class="mobileSliderImageClass" />
            </div>
          </SwiperSlide>
        </Swiper>

        <!-- Masaüstü için grid düzenleri -->
        <template v-else-if="!isMobile && bannerCount >1">
          <!-- İki banner: yan yana iki kolon -->
          <div v-if="bannerCount === 2" class="grid grid-cols-2 gap-4">
            <template v-for="(banner, index) in displayBanners" :key="banner.src + index">
              <NuxtLink
                v-if="resolveBannerLink(banner) && !isBannerExternal(banner)"
                :to="resolveBannerLink(banner)"
                :class="desktopWrapperClass"
              >
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy" 
                  :class="desktopImageClass" />
              </NuxtLink>
              <a
                v-else-if="resolveBannerLink(banner)"
                :href="resolveBannerLink(banner)"
                target="_blank"
                rel="noopener noreferrer"
                :class="desktopWrapperClass"
              >
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy" 
                  :class="desktopImageClass" />
              </a>
              <div v-else :class="desktopWrapperClass">
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </div>
            </template>
          </div>
          <!-- Üç banner: üçlü kolon yapısı -->
          <div v-else-if="bannerCount === 3" class="grid grid-cols-3 gap-4">
            <template v-for="(banner, index) in displayBanners" :key="banner.src + index">
              <NuxtLink
                v-if="resolveBannerLink(banner) && !isBannerExternal(banner)"
                :to="resolveBannerLink(banner)"
                :class="desktopWrapperClass"
              >
                <NuxtImg format="webp" :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </NuxtLink>
              <a
                v-else-if="resolveBannerLink(banner)"
                :href="resolveBannerLink(banner)"
                target="_blank"
                rel="noopener noreferrer"
                :class="desktopWrapperClass"
              >
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </a>
              <div v-else :class="desktopWrapperClass">
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </div>
            </template>
          </div>
          <!-- Üçten fazla: önce 3 kolon, geniş ekranda 4 kolon -->
          <div v-else class="grid grid-cols-3 lg:grid-cols-4 gap-4">
            <template v-for="(banner, index) in displayBanners" :key="banner.src + index">
              <NuxtLink
                v-if="resolveBannerLink(banner) && !isBannerExternal(banner)"
                :to="resolveBannerLink(banner)"
                :class="desktopWrapperClass"
              >
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </NuxtLink>
              <a
                v-else-if="resolveBannerLink(banner)"
                :href="resolveBannerLink(banner)"
                target="_blank"
                rel="noopener noreferrer"
                :class="desktopWrapperClass"
              >
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </a>
              <div v-else :class="desktopWrapperClass">
                <NuxtImg :src="banner.src" :alt="banner.alt" loading="lazy"
                  :class="desktopImageClass" />
              </div>
            </template>
          </div>
        </template>
      </div>

      <div class="bg-sky-100 sm:rounded-xl" :class="wrapperClass" style="contain: layout style">
        <!-- Başlık ve aksiyon alanı -->
        <div class="flex items-center justify-between px-6 md:px-8 py-4 sm:py-5">
          <slot name="header" :title="title">
            <div class="flex items-center gap-2">
              <div class="w-1 sm:w-1.5 h-6 sm:h-8 rounded-full" :class="accentColorClass" style="transform: translateZ(0)" />
              <h2 class="text-xl sm:text-2xl font-bold text-gray-800">
                {{ title }}
              </h2>
            </div>
          </slot>

          <slot name="action">
            <NuxtLink v-if="viewAllLink"
              :to="viewAllLink"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-500 rounded-full bg-orange-50 hover:bg-orange-100 transition-colors"
              style="transform: translateZ(0)">
              {{ viewAllLabel }}
              <UIcon name="i-heroicons-arrow-right-20-solid" class="w-4 h-4" />
            </NuxtLink>
          </slot>
        </div>

        <!-- Ürün karuseli / boş durum alanı -->
        <div class="relative  md:pl-0 pt-3 pb-4 sm:pb-6 overflow-visible" style="contain: layout style">
          <slot v-if="hasProducts" :products="products">
            <FrontendProductCarouselMobileSimpleScroll v-if="isMobile" :products="products"
              @add-to-cart="handleAddToCart" @toggle-favorite="handleToggleFavorite" />

            <FrontendProductCarouselDesktopButtonCarousel v-else :products="products"
              @add-to-cart="handleAddToCart" @toggle-favorite="handleToggleFavorite" />
          </slot>

          <slot v-else name="empty">
            <div class="px-6 py-10 text-center text-sm text-gray-500">
              {{ emptyStateMessage }}
            </div>
          </slot>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode } from 'swiper/modules'

// Bileşen varsayılan metin ve veri kaynaklarını burada tanımlıyoruz
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  products: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  viewAllLink: {
    type: [String, Object],
    default: null
  },
  viewAllLabel: {
    type: String,
    default: 'Tümünü Gör'
  },
  accentColorClass: {
    type: [String, Array, Object],
    default: 'bg-orange-500'
  },
  wrapperClass: {
    type: [String, Array, Object],
    default: () => []
  },
  emptyStateMessage: {
    type: String,
    default: 'Gösterilecek ürün bulunamadı.'
  },
  banner: {
    type: [String, Object],
    default: null
  },
  bannerAlt: {
    type: String,
    default: ''
  },
  banners: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['add-to-cart', 'toggle-favorite'])

const { isMobile } = useDevice()

// Ürün var mı kontrolü, alt slotları buna göre gösteriyoruz
const hasProducts = computed(() => props.products?.length > 0)

const displayBanners = computed(() => {
  if (Array.isArray(props.banners) && props.banners.length > 0) {
    return props.banners
  }

  if (props.banner) {
    return [props.banner]
  }

  return []
})

const bannerCount = computed(() => displayBanners.value.length)

const resolveBannerLink = (banner) => {
  if (!banner) {
    return null
  }

  return banner.link ?? banner.link_url ?? null
}

const isBannerExternal = (banner) => {
  const link = resolveBannerLink(banner)

  if (!link) {
    return false
  }

  return banner?.is_external ?? /^https?:\/\//i.test(link)
}

// Mobil swiper için gerekli modüller ve ayarlar
const bannerSwiperModules = [FreeMode]
const bannerFreeModeOptions = {
  enabled: true,
  momentum: true,
  momentumRatio: 0.75,
  momentumVelocityRatio: 0.85
}

const singleMobileWrapperClass = 'block w-full focus:outline-none focus:ring-2 focus:ring-orange-400'
const sliderWrapperClass = 'block w-full focus:outline-none focus:ring-2 focus:ring-orange-400'
const desktopWrapperClass = 'block w-full focus:outline-none focus:ring-2 focus:ring-orange-400'
const mobileSingleImageClass = 'w-full  object-cover rounded-lg shadow-sm'
const mobileSliderImageClass = 'w-full  object-cover rounded-lg shadow-sm'
const desktopImageClass = 'w-full  object-cover rounded-lg shadow-sm'

// Alt bileşenlerden gelen aksiyonları ebeveyne iletiyoruz
const handleAddToCart = (productData) => {
  emits('add-to-cart', productData)
}

const handleToggleFavorite = (product) => {
  emits('toggle-favorite', product)
}
</script>

<style scoped>
@import 'swiper/css';
@import 'swiper/css/free-mode';

.banner-swiper {
  margin: 0 -1rem;
  padding: 0 1rem 0.5rem;
}

.banner-swiper :deep(.swiper-wrapper) {
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
}

.banner-swiper :deep(.swiper-slide) {
  height: auto;
}
</style>
